import { CardDimension } from '@/interfaces/raDashboard.interface'
import { RADashboardCardId, RADashboardCardSize } from './cards-enums'

const PointCardSizes: Record<string, CardDimension> = {
  [RADashboardCardSize.ExtraSmall]: { width: 2, height: 1 },
  [RADashboardCardSize.Small]: { width: 2, height: 2 },
  [RADashboardCardSize.Medium]: { width: 3, height: 2 },
  [RADashboardCardSize.Large]: { width: 3, height: 3 },
}

// Return value + sparkline cards — wider than the plain data-point cards so three at Small fill the row.
const ReturnChartSizes: Record<string, CardDimension> = {
  [RADashboardCardSize.Small]: { width: 4, height: 2 },
  [RADashboardCardSize.Medium]: { width: 6, height: 3 },
  [RADashboardCardSize.Large]: { width: 8, height: 4 },
}

const SizeLookup: Record<string, Record<string, CardDimension>> = {
  [RADashboardCardId.FactorLensRisk]: {
    [RADashboardCardSize.ExtraSmall]: { width: 6, height: 6 },
    [RADashboardCardSize.Small]: { width: 7, height: 7 },
    [RADashboardCardSize.Medium]: { width: 8, height: 8 },
    [RADashboardCardSize.Large]: { width: 10, height: 10 },
    [RADashboardCardSize.ExtraLarge]: { width: 12, height: 12 },
    [RADashboardCardSize.Wide]: { width: 10, height: 8 },
    [RADashboardCardSize.ExtraWide]: { width: 12, height: 8 },
  },
  [RADashboardCardId.FactorLensGridRisk]: {
    [RADashboardCardSize.ExtraSmall]: { width: 5, height: 3 },
    [RADashboardCardSize.Small]: { width: 6, height: 4 },
    [RADashboardCardSize.Medium]: { width: 8, height: 5 },
    [RADashboardCardSize.Large]: { width: 10, height: 6 },
    [RADashboardCardSize.Wide]: { width: 9, height: 5 },
    [RADashboardCardSize.ExtraWide]: { width: 12, height: 5 },
  },
  [RADashboardCardId.FactorWaterfallRisk]: {
    [RADashboardCardSize.ExtraSmall]: { width: 4, height: 2 },
    [RADashboardCardSize.Small]: { width: 6, height: 2 },
    [RADashboardCardSize.Medium]: { width: 8, height: 3 },
    [RADashboardCardSize.Large]: { width: 10, height: 4 },
    [RADashboardCardSize.Wide]: { width: 9, height: 3 },
    [RADashboardCardSize.ExtraWide]: { width: 12, height: 3 },
  },
  [RADashboardCardId.FactorBreakdownRisk]: {
    [RADashboardCardSize.ExtraSmall]: { width: 5, height: 2 },
    [RADashboardCardSize.Small]: { width: 6, height: 3 },
    [RADashboardCardSize.Medium]: { width: 8, height: 4 },
    [RADashboardCardSize.Large]: { width: 10, height: 5 },
    [RADashboardCardSize.Wide]: { width: 9, height: 4 },
    [RADashboardCardSize.ExtraWide]: { width: 12, height: 4 },
  },
  [RADashboardCardId.MetricsSummaryRisk]: {
    [RADashboardCardSize.Small]: { width: 6, height: 3 },
    [RADashboardCardSize.Medium]: { width: 6, height: 4 },
    [RADashboardCardSize.Large]: { width: 6, height: 5 },
    [RADashboardCardSize.ExtraLarge]: { width: 12, height: 5 },
    [RADashboardCardSize.Wide]: { width: 9, height: 4 },
    [RADashboardCardSize.ExtraWide]: { width: 12, height: 4 },
  },
  [RADashboardCardId.Point_TrailingRisk]: {
    [RADashboardCardSize.Small]: { width: 4, height: 3 },
    [RADashboardCardSize.Medium]: { width: 6, height: 4 },
    [RADashboardCardSize.Large]: { width: 8, height: 5 },
    [RADashboardCardSize.ExtraLarge]: { width: 12, height: 8 },
    [RADashboardCardSize.Wide]: { width: 9, height: 4 },
    [RADashboardCardSize.ExtraWide]: { width: 12, height: 4 },
  },
  [RADashboardCardId.HoldingsTopBottom]: {
    [RADashboardCardSize.Small]: { width: 6, height: 3 },
    [RADashboardCardSize.Medium]: { width: 6, height: 4 },
    [RADashboardCardSize.Large]: { width: 6, height: 5 },
    [RADashboardCardSize.ExtraLarge]: { width: 12, height: 5 },
    [RADashboardCardSize.Wide]: { width: 9, height: 4 },
    [RADashboardCardSize.ExtraWide]: { width: 12, height: 4 },
  },
  [RADashboardCardId.HoldingsRiskVisualization]: {
    [RADashboardCardSize.Small]: { width: 6, height: 3 },
    [RADashboardCardSize.Medium]: { width: 6, height: 4 },
    [RADashboardCardSize.Large]: { width: 6, height: 5 },
    [RADashboardCardSize.ExtraLarge]: { width: 12, height: 8 },
    [RADashboardCardSize.Wide]: { width: 9, height: 4 },
    [RADashboardCardSize.ExtraWide]: { width: 12, height: 4 },
  },
  [RADashboardCardId.HoldingsContributionRisk]: {
    [RADashboardCardSize.ExtraSmall]: { width: 6, height: 4 },
    [RADashboardCardSize.Small]: { width: 9, height: 6 },
    [RADashboardCardSize.Medium]: { width: 12, height: 6 },
    [RADashboardCardSize.Large]: { width: 12, height: 8 },
    [RADashboardCardSize.ExtraLarge]: { width: 12, height: 10 },
    [RADashboardCardSize.Wide]: { width: 9, height: 4 },
    [RADashboardCardSize.ExtraWide]: { width: 12, height: 4 },
  },
  [RADashboardCardId.Chart_DailyReturn]: {
    [RADashboardCardSize.Small]: { width: 6, height: 3 },
    [RADashboardCardSize.Medium]: { width: 9, height: 4 },
    [RADashboardCardSize.Large]: { width: 12, height: 4 },
  },
  // Portfolio/Benchmark/Active Return value + sparkline.
  [RADashboardCardId.Chart_PortfolioReturn]: ReturnChartSizes,
  [RADashboardCardId.Chart_BenchmarkReturn]: ReturnChartSizes,
  [RADashboardCardId.Chart_ActiveReturn]: ReturnChartSizes,
  [RADashboardCardId.Chart_FactorAttribution]: {
    [RADashboardCardSize.Small]: { width: 8, height: 4 },
    [RADashboardCardSize.Medium]: { width: 10, height: 5 },
    [RADashboardCardSize.Large]: { width: 12, height: 5 },
  },
  [RADashboardCardId.Table_Holdings]: {
    [RADashboardCardSize.Small]: { width: 9, height: 6 },
    [RADashboardCardSize.Medium]: { width: 10, height: 6 },
    [RADashboardCardSize.Large]: { width: 12, height: 6 },
    [RADashboardCardSize.ExtraLarge]: { width: 12, height: 10 },
  },
  [RADashboardCardId.Table_HoldingsTopBottom]: {
    [RADashboardCardSize.Small]: { width: 6, height: 3 },
    [RADashboardCardSize.Medium]: { width: 6, height: 4 },
    [RADashboardCardSize.Large]: { width: 6, height: 5 },
    [RADashboardCardSize.ExtraLarge]: { width: 12, height: 5 },
    [RADashboardCardSize.Wide]: { width: 9, height: 4 },
    [RADashboardCardSize.ExtraWide]: { width: 12, height: 4 },
  },
  [RADashboardCardId.Chart_HoldingsBubble]: {
    [RADashboardCardSize.Small]: { width: 6, height: 3 },
    [RADashboardCardSize.Medium]: { width: 6, height: 4 },
    [RADashboardCardSize.Large]: { width: 6, height: 5 },
    [RADashboardCardSize.ExtraLarge]: { width: 12, height: 8 },
    [RADashboardCardSize.Wide]: { width: 9, height: 4 },
    [RADashboardCardSize.ExtraWide]: { width: 12, height: 4 },
  },
}

const SIZE_ORDER: RADashboardCardSize[] = [
  RADashboardCardSize.ExtraSmall,
  RADashboardCardSize.Small,
  RADashboardCardSize.Medium,
  RADashboardCardSize.Large,
  RADashboardCardSize.ExtraLarge,
  RADashboardCardSize.Wide,
  RADashboardCardSize.ExtraWide,
]

export function getCardAvailableSizes(cardId: string): RADashboardCardSize[] {
  const sizes = cardId.startsWith('point_') ? PointCardSizes : SizeLookup[cardId]
  if (!sizes) return []
  return SIZE_ORDER.filter((s) => s in sizes)
}

/**
 * Returns the dimensions assoziated with a card type's size. If the specified size is not found, it returns the first size defined.
 * @param cardId
 * @param size
 * @returns
 */
export function lookupCardSize(cardId: RADashboardCardId | string, size: RADashboardCardSize | string) {
  if (cardId.startsWith('empty_')) {
    const [w, h] = cardId.replace('empty_', '').split('x').map(Number)
    return { width: w, height: h }
  }

  if (size.startsWith(RADashboardCardSize.Custom)) {
    return parseCustomSize(size);
  }

  if (cardId.startsWith('point_')) {
    return PointCardSizes[size] ?? PointCardSizes[RADashboardCardSize.Medium]
  }

  const sizes = SizeLookup[cardId]
  if (sizes) {
    if (sizes[size]) return sizes[size]
    const sizeOptions = Object.entries(sizes)
    if (sizeOptions.length > 0) return sizeOptions[0][1]
  }
  return null
}

export function isCustomSizeValid(cardId: string, size: string): boolean {
  const custom = parseCustomSize(size)
  if (!custom) return false

  const smallest = getSmallestCardSize(cardId)
  if (!smallest) return false

  return custom.width >= smallest.width && custom.height >= smallest.height
}

export function parseCustomSize(size: string): CardDimension | null {
  if (!size.startsWith(RADashboardCardSize.Custom)) return null
  
  const [w, h] = size.replace(RADashboardCardSize.Custom, '').split('x').map(Number)
  if(isNaN(w) || w <= 0 || isNaN(h) || h <= 0) return null
  
  return { width: w, height: h }
}

export function getSmallestCardSize(cardId: string): CardDimension | null {
  const smallest = getCardAvailableSizes(cardId)[0]
  if (!smallest) return null
  return lookupCardSize(cardId, smallest)
}
